import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";
import { label } from "@/lib/labels";
import { GANTT_COLORS } from "@/components/planning/Gantt";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Check, Crosshair } from "lucide-react";
import { differenceInCalendarDays, parseISO } from "date-fns";

const dot = (type) => (GANTT_COLORS[type] || GANTT_COLORS.annet).edge;

/**
 * Conflict list with direct resolve actions. Used inline (desktop) and inside a Sheet (mobile).
 */
export const ConflictRail = ({ conflicts = [], onResolve, onLocate, canWrite, className, loading }) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = conflicts.map((c) => ({ ...c, days: Math.max(1, differenceInCalendarDays(parseISO(c.overlap_end), parseISO(c.overlap_start))) }));
    arr.sort((a, b) => (a.overlap_start < b.overlap_start ? -1 : 1));
    if (!s) return arr;
    return arr.filter((c) => [c.module_code, c.a.project_name, c.a.project_number, c.b.project_name, c.b.project_number].some((v) => (v || "").toLowerCase().includes(s)));
  }, [conflicts, q]);

  return (
    <div className={cn("flex h-full flex-col", className)} data-testid="planning-conflict-rail">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b px-4">
        <span className="text-[13px] font-medium">Konflikter</span>
        <span className={cn("font-mono text-[12px] tabular", conflicts.length ? "text-rose-600 dark:text-rose-300" : "text-muted-foreground")} data-testid="conflict-rail-count">{conflicts.length}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">{conflicts.length === 0 ? "Ingen dobbelbookinger" : "dobbelbookinger å løse"}</span>
      </div>
      {conflicts.length > 4 && (
        <div className="border-b px-3 py-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer modul eller prosjekt…" className="h-8 border-transparent bg-muted/60 text-xs focus-visible:bg-background" data-testid="conflict-rail-search" />
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="p-4 text-xs text-muted-foreground">Laster…</div>
        ) : conflicts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center" data-testid="conflict-rail-empty">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600"><Check className="h-4 w-4" /></span>
            <div className="text-[13px] font-medium">Alt er i orden</div>
            <div className="text-xs text-muted-foreground">Ingen moduler er dobbelbooket. Nye konflikter vises her automatisk.</div>
          </div>
        ) : list.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground">Ingen treff på «{q}».</div>
        ) : (
          <ul className="divide-y divide-border/70">
            {list.map((c) => (
              <li key={`${c.module_id}-${c.a.id}-${c.b.id}`} className="px-4 py-3" data-testid="planning-conflict-item">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-[12.5px] font-semibold">{c.module_code}</span>
                  <span className="text-[11px] text-muted-foreground tabular">{fmtDate(c.overlap_start, "d. MMM")} – {fmtDate(c.overlap_end, "d. MMM")} · {c.days} d overlapp</span>
                </div>
                <div className="mt-2 space-y-1">
                  {[c.a, c.b].map((a, j) => (
                    <button key={a.id} type="button" onClick={() => onResolve?.(a, c)} className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 -mx-2 text-left text-[12px] hover:bg-muted/70 focus-visible:ring-1 focus-visible:ring-ring" data-testid={`conflict-alloc-${j === 0 ? "a" : "b"}-${a.id}`}>
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", dot(a.type))} title={label(a.type)} />
                      <span className="min-w-0 flex-1 truncate"><span className="font-mono text-muted-foreground">{a.project_number}</span> <span className="font-medium">{a.project_name || "(uten prosjekt)"}</span></span>
                      <span className="shrink-0 tabular text-muted-foreground">{fmtDate(a.start_date, "d. MMM")} – {fmtDate(a.end_date, "d. MMM")}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-3 text-[12px]">
                  {canWrite && <button type="button" className="font-medium text-primary hover:underline" onClick={() => onResolve?.(c.b, c)} data-testid={`conflict-resolve-${c.b.id}`}>Løs konflikt</button>}
                  <button type="button" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => onLocate?.(c)} data-testid={`conflict-locate-${c.b.id}`}><Crosshair className="h-3 w-3" /> Vis i tidslinje</button>
                  <span className="ml-auto text-[11px] text-muted-foreground">{label(c.a.status)} · {label(c.b.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
